import { useEffect, useMemo, useState } from "react";
// COMPONENTS
import Cards from "../../components/Cards";

//CSS
import "./integrations.css";

import {
  BrandGAds,
  BrandGAnalytics,
  BrandMeta,
  BrandShopify,
  MenuIcon,
  PlusIcon,
  TrashIcon,
  YoutubeLogo
} from "../../components/Icons/iconImages";
import { useSelector } from "react-redux";
import {
  useGetAnalyticStatusQuery,
  useDeleteWorkspaceMutation,
  useLazySaveShopifyUserDataQuery,
} from "../../services/integrationSlice";
import { useGetProfilesQuery } from "../../services/createProfile";

import { useLocation, useNavigate } from "react-router-dom";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { Button } from "../../components/Buttons";
import { useForm } from "react-hook-form";
import IconRings from "../../components/IconRings";
import { Table, Tabs, Modal as AntdModal } from "antd";
import { CustomIntegrationComponent, IntegrationComponent } from "./IntegrationComponent";
import { toast } from "../../components/Toast/toast";
import { useGetGeneralSettingsQuery } from "../../services/settingsSlice";
import { useOnboardingTour } from '../../Hooks/useOnboardingTour';
import { getTour } from '../../components/OnboardingTour/tourConfigs';
import OnboardingTour from '../../components/OnboardingTour/SafeOnboardingTour';
import TourDebugHelper from '../../components/OnboardingTour/TourDebugHelper';
import useIsInIframe from "../../Hooks/useIsInIframe";

const NestedIntegrations = () => {
  const getWorkspaceId = useSelector((state) => state?.static?.workSpaceId);
  const isInIframe = useIsInIframe();

  // Onboarding tour for integrations
  const integrationsTourData = getTour('integrations');
  const {
    runTour,
    currentTour,
    closeTour,
    skipTour,
    resetTours,
    startTour
  } = useOnboardingTour(integrationsTourData, {
    delay: 1000, // Show tour after 1 second
    autoStart: false, // Don't auto-start on integrations page
    chainToNext: true // Enable tour chaining to continue sequence
  });

  // Listen for manual tour start events
  useEffect(() => {
    const handleStartTour = (event) => {
      const tourData = event.detail;
      // Only start this tour if it matches the integrations tour and no tour is currently running
      if (tourData && tourData.id === 'integrations-tour' && !runTour) {
        startTour(tourData, tourData.isChainedTour || false);
      }
    };

    window.addEventListener('startTour', handleStartTour);
    return () => {
      window.removeEventListener('startTour', handleStartTour);
    };
  }, [startTour, runTour]);

  const iconMap = useMemo(() => ({
    BrandGAnalytics: BrandGAnalytics,
    BrandShopify: BrandShopify,
    BrandMeta: BrandMeta,
    BrandGAds: BrandGAds,
    BrandYoutube: YoutubeLogo,
  }), []);


  const { refetch } = useGetAnalyticStatusQuery();

  useEffect(() => {
    if (getWorkspaceId) {
      refetch();
    }
  }, [getWorkspaceId])


  const { control, getValues, setError, reset, watch } = useForm({
    defaultValues: {
      dontShowAgain: false,
    },
    shopUrl: '',
    shopAccessToken: '',
  });

  return (
    <>
      <section className="flex flex-col w-full gap-4">
        <div className="flex justify-between items-start border-b border-solid border-gray-300 pb-4">
          <div className="flex-1">
            <h5 className="font-normal text-sm sm:text-base">To get {isInIframe ? "Ask AI" : "Oogwai"} to answer your questions, allow us to fetch data from the sources below</h5>
          </div>
          {/* <Button shape="circle" type="text" size="small" icon={<MenuIcon className="w-4 h-4" />} /> */}
        </div>

        <IntegrationComponent
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          iconMap={iconMap}
          control={control}
          setError={setError}
          reset={reset}
          getValues={getValues}
          watch={watch}
        />
      </section>
      <section className="flex flex-col w-full gap-4 mt-4">
        <div className="flex justify-between items-start border-b border-solid border-gray-300 pb-4">
          <div className="flex-1">
            <h4 className="text-base sm:text-lg">Custom BigQuery</h4>
            <h5 className="font-normal text-sm sm:text-base">Set up custom data sources</h5>
          </div>
          {/* <Button shape="circle" type="text" size="small" icon={<MenuIcon className="w-4 h-4" />} /> */}
        </div>

        <CustomIntegrationComponent
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          iconMap={iconMap}
          control={control}
          watch={watch}
          reset={reset}
          setError={setError}
          getValues={getValues}
        />
      </section>

      {/* Onboarding Tour - disabled in iframe */}
      {!isInIframe && (
        <OnboardingTour
          isOpen={runTour}
          tourData={currentTour}
          onComplete={closeTour}
          onSkip={skipTour}
        />
      )}

      {/* Tour Debug Helper - disabled in iframe */}
      {!isInIframe && <TourDebugHelper />}
    </>
  );
};

const NestedProfiles = () => {
  const { data: profilesData, isLoading } = useGetProfilesQuery({
    refetchOnReconnect: true,
  });

  const [deleteProfile, { isLoading: isLoadingDelete }] = useDeleteWorkspaceMutation();
  const navigate = useNavigate();
  const [workspaceId, setWorkspaceId] = useState("");
  const { refreshWorkspaces, getCurrentWorkspaceObjectId, switchWorkspace } = useWorkspace();

  const [isModalOpenProfile, setIsModalOpenProfile] = useState(false);

  const showModalProfile = () => {
    setIsModalOpenProfile(true);
  };

  const handleCancelProfile = () => {
    setIsModalOpenProfile(false);
  };

  const { data: generalSettings, refetch: refetchGeneralSettings } = useGetGeneralSettingsQuery('');
  const currentWorkspaceId = generalSettings?.settings?.current_workspace;

  const columns = [
    {
      title: 'Workspace Id',
      dataIndex: 'workspace_id',
      key: 'workspace_id',
      width: '25%',
      ellipsis: true,
    },
    {
      title: 'Profile Description',
      dataIndex: 'profile_description',
      key: 'profile_description',
      width: '30%',
      ellipsis: true,
    },
    {
      title: 'Properties',
      dataIndex: 'properties',
      key: 'properties',
      width: '25%',
      render: (text, record) => {
        const propertiesKeys = Object.keys(record?.properties);
        const propertiesValues = Object.entries(record?.properties)?.filter(([key, value]) => value ?? key)?.map((res) => res[0]);

        return (
          <>
            {<div className="flex flex-col gap-2">
              {propertiesKeys.every(propertyKey => !record?.properties[propertyKey]) ? (
                <p className="text-[#535862] text-sm font-normal">No Integration found</p>
              ) : (
                propertiesValues?.map(propertyKey => (
                  <p key={propertyKey} className="text-[#535862] text-sm font-normal">
                    {propertyKey.charAt(0).toUpperCase() + propertyKey.slice(1)}
                  </p>
                ))
              )}
            </div>}
          </>
        )
      }
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: '20%',
      align: 'right',
      render: (text, record) => {
        const isActive = record.workspace_id === currentWorkspaceId;
        if (isActive) {
          return (
            <div className="flex items-center gap-2 justify-end">
              <span title="Active Profile" className="bg-green-100 rounded-full px-2 py-1 text-xs text-green-700 font-semibold whitespace-nowrap">
                Current Workspace
              </span>
            </div>
          );
        }
        return (
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
            {profilesData?.workspaces?.length > 1 &&
              <Button
                type="text"
                size="small"
                onClick={() => { showModalProfile(); setWorkspaceId(record._id) }}
              >
                Delete
              </Button>}
            <Button
              size="small"
              type="text"
              variant="text"
              color="primary"
              onClick={() => navigate(`/edit-profile/${record._id}`, {
                state: {
                  data: record,
                  activeTab: '2'
                }
              })}
            >
              Edit
            </Button>
          </div>
        );
      }
    },
  ];

  // if (isLoading) {
  //   return <div>Loading...</div>;
  // }

  return (
    <>
      <section className="flex flex-col w-full gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-solid border-gray-300 pb-4 gap-3">
          <div className="flex-1">
            <h5 className="font-normal">Add, remove or edit your integration workspaces here</h5>
          </div>
          <Button
            type="primary"
            loading={isLoading}
            onClick={() => navigate('/create-profile')}
            icon={<PlusIcon className="w-4 h-4" />}
            className="w-full sm:w-auto"
            data-tour="create-workspace-button"
          >
            Create Workspace
          </Button>
        </div>
        {
          profilesData?.workspaces?.length === 0 ? (
            <div className="flex flex-col items-center mt-4 justify-center gap-4">
              <h4>No profiles found</h4>
              <p className="text-center">You haven't created any profiles yet. Click on the button above to add a new profile.</p>
            </div>
          ) : <Table
            className="commonTable mt-5"
            dataSource={profilesData?.workspaces}
            columns={columns}
            pagination={false}
            sticky
            scroll={{ x: "max-content" }}
            loading={{
              spinning: isLoading,
              // indicator: <Loading type="loading"  />
            }}
            data-tour="workspaces-table"
          />
        }
      </section>

      <AntdModal centered defaultClose={false} className="xl:max-w-[40%] lg:max-w-[50%] md:max-w-[65%] sm:max-w-[85%] max-w-[95%] ga_redirect_modal" open={isModalOpenProfile}>
        <div className="flex flex-row items-start gap-3 sm:gap-5 p-3 sm:p-5">
          <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className="hidden sm:block">
            <div className="bg-[#FEE4E2] p-3 w-fit h-fit rounded-full">
              <TrashIcon className="text-[#D92D20] w-5 h-5" />
            </div>
          </IconRings>

          <div className="z-10 flex-1">
            <div className="flex items-center gap-2 sm:hidden mb-2">
              <div className="bg-[#FEE4E2] p-2 w-fit h-fit rounded-full">
                <TrashIcon className="text-[#D92D20] w-4 h-4" />
              </div>
            </div>
            <h4 className="text-base sm:text-lg">Delete WorkSpace</h4>
            <p className="text-xs sm:text-sm mt-1">Are you sure you want to delete this workspace?</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 items-stretch sm:items-center p-3 sm:p-5">
          <Button
            variant="outlined"
            onClick={(e) => {
              handleCancelProfile();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={isLoadingDelete}
            loading={isLoadingDelete}
            onClick={async (e) => {
              const currentWorkspaceId = getCurrentWorkspaceObjectId();
              const isCurrentWorkspaceBeingDeleted = workspaceId === currentWorkspaceId;

              try {
                const response = await deleteProfile(workspaceId).unwrap();
                if (response) {
                  toast.success(response?.message || 'Workspace removed successfully');

                  // Refresh both workspaces list and user settings to trigger WorkspaceContext re-initialization
                  await Promise.all([
                    refreshWorkspaces(),
                    refetchGeneralSettings()
                  ]);

                  handleCancelProfile();

                 
                }
              } catch (error) {
                toast.error(error?.data?.error || 'Unexpected error');
                handleCancelProfile();
              }
            }}
          >
            Delete
          </Button>
        </div>
      </AntdModal>
    </>
  );
}


const items = [
  {
    key: '1',
    label: 'Integrations',
    children: <NestedIntegrations />,
  },
  {
    key: '2',
    label: 'Workspaces',
    children: <NestedProfiles />,
  },
];

const Integrations = () => {

  const [activeTab, setActiveTab] = useState('1');
  const location = useLocation();
  const navigate = useNavigate();
  const { switchWorkspace, currentWorkspace, refreshWorkspaces, getCurrentWorkspaceObjectId } = useWorkspace();

  // Workspace tour setup - must be in parent component to be always mounted
  const workspaceTourData = getTour('workspace');
  const {
    runTour: runWorkspaceTour,
    currentTour: currentWorkspaceTour,
    closeTour: closeWorkspaceTour,
    skipTour: skipWorkspaceTour,
    startTour: startWorkspaceTour
  } = useOnboardingTour(workspaceTourData, {
    delay: 1000,
    autoStart: false,
    chainToNext: true // Enable tour chaining to continue sequence
  });

  // Listen for workspace tour start and switch to workspaces tab
  useEffect(() => {
    const handleStartTour = (event) => {
      const tourData = event.detail;
      if (tourData && tourData.id === 'workspace-tour' && !runWorkspaceTour) {
        // Switch to Workspaces tab first
        setActiveTab('2');
        // Start tour after a short delay to let tab switch complete
        setTimeout(() => {
          startWorkspaceTour(tourData, tourData.isChainedTour || false);
        }, 500);
      }
    };

    window.addEventListener('startTour', handleStartTour);
    return () => {
      window.removeEventListener('startTour', handleStartTour);
    };
  }, [startWorkspaceTour, runWorkspaceTour]);

  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state?.activeTab);
      navigate(location.pathname);
    }
  }, [location]);

  // Listen for tour navigation events
  useEffect(() => {
    const handleTourNavigate = (event) => {
      const { path, state } = event.detail;
      if (state?.activeTab) {
        setActiveTab(state.activeTab);
      }
      if (path && path !== location.pathname) {
        navigate(path, { state });
      }
    };

    window.addEventListener('tourNavigate', handleTourNavigate);
    return () => {
      window.removeEventListener('tourNavigate', handleTourNavigate);
    };
  }, [navigate, location.pathname]);

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token")?.replace(/ /g, '+');
  const email = urlParams.get("email")?.replace(/ /g, '+');
  const shopDomain = urlParams.get("shop-domain")?.replace(/ /g, '+');
  const workspaceParam = urlParams.get("workspace");

  // Handle workspace switching from URL parameter
  useEffect(() => {
    if (workspaceParam && currentWorkspace !== workspaceParam) {
      console.log('Switching to workspace from URL:', workspaceParam);
      switchWorkspace(workspaceParam).then(() => {
        console.log('Successfully switched to workspace:', workspaceParam);
        // Remove the workspace parameter from URL after switching
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('workspace');
        window.history.replaceState({}, '', newUrl);
      }).catch((error) => {
        console.error('Failed to switch workspace:', error);
      });
    }
  }, [workspaceParam, currentWorkspace, switchWorkspace]);

  const [saveShopifyUserData, { isLoading }] = useLazySaveShopifyUserDataQuery();

  // http://localhost:8000/shopify/token?token=shpua_4fb514e6dc562ec3b85da3ff53af1617&email=patelnasib06@gmail.com&shop-domain=quickstart-901f2ccb.myshopify.com
  const isParamsValid = urlParams.has('token') && urlParams.has('email');
  const [isDataSaved, setIsDataSaved] = useState(false);

  useEffect(() => {
    if (!isParamsValid) return;

    const shopifyInternal = localStorage.getItem('shopify_internal') === 'true';
    const emailParam = shopifyInternal
      ? { oogwai_email: localStorage.getItem('oogw.ai_user_email') }
      : { shopify_email: email };

    saveShopifyUserData({ token, ...emailParam, shopDomain })
      .unwrap()
      .then((response) => {
        if (response) {
          setIsDataSaved(true);
          toast.success(response?.message || 'Shopify user data saved successfully');

          if (response?.message) {
            if (shopifyInternal) {
              localStorage.setItem('shopify_internal', 'false');
            } else {
              localStorage.setItem("oogw.ai_user", email);
              localStorage.setItem("oogw.ai_user_type", 'Shopify');
              localStorage.setItem('oogw.ai_shopify_email', response?.email);
              localStorage.setItem('workspace_id', response?.workspace_id);
            }
            navigate('/integrations');
          }
        }
      })
      .catch((error) => {
        if (shopifyInternal) {
          localStorage.setItem('shopify_internal', 'false');
        }
        console.log(error);
        toast.error(error?.data?.error || error?.message || 'Unexpected error');
      });
  }, [isParamsValid, saveShopifyUserData, token, email, shopDomain]);

  const handleNavigation = () => {
    navigate('/shopify/view-raw-data', { state: { email: email } });
  };

  // DIRECT SHOPIFY FOR VIEW RAW DATA
  if (isParamsValid) {
    return (
      <div className="bg-white p-4 rounded-2xl">
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <>
            <p className="text-sm">Token: {token}</p>
            <p className="text-sm">Email: {email}</p>
            {isDataSaved && (
              <div className="flex items-center gap-4">
                <Button
                  type="link"
                  className="mt-4"
                  onClick={() => navigate('/')}
                >
                  Go to Home Page
                </Button>
                <Button
                  type="primary"
                  className="mt-4"
                  onClick={handleNavigation}
                >
                  View Raw Data
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  const onChange = (key) => {
    setActiveTab(key)
  };

  return (
    <div className="pt-3 px-2 sm:px-0">
      <Cards classname={'basis-3/4 p-4 mt-4 sm:p-6 md:p-8 pt-1'} childClassName={"flex-col justify-between"} variant="secondary">
        <Tabs
          className="integration-tabs"
          defaultActiveKey={activeTab}
          items={items}
          centered={false}
          activeKey={activeTab}
          value={activeTab}
          onChange={onChange}
          indicator={{ color: '#1F7A50' }}
        />
      </Cards>

      {/* Workspace Tour */}
      <OnboardingTour
        isOpen={runWorkspaceTour && window.self === window.top}
        tourData={currentWorkspaceTour}
        onComplete={closeWorkspaceTour}
        onSkip={skipWorkspaceTour}
      />
    </div>
  );
}


export default Integrations;